import React, { useMemo } from 'react';

import { ButtonWithIcon } from '@/components/global/ButtonWithIcon';

import { TCard } from '@/services/requests/Recommendation/types/template4';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';
import { isHttpLink } from '@/helpers/StringHelper/isHttpLink';
import { getCTA } from '@/helpers/CtaHelper';

type TProps = {
  data: TCard;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, cardId: string) => void;
};

export const Card = React.memo(({ data, onClick }: TProps) => {
  const {
    id,
    icon,
    title,
    detail: { description },
  } = data;
  const link = getCTA(data.cta, 'web')?.link || '';

  const styleCardLink = useMemo(() => {
    return link ? { cursor: 'pointer' } : { cursor: 'default' };
  }, [link]);

  const formattedLink = useMemo(() => {
    return isHttpLink(link) ? link : undefined;
  }, [link]);

  const isDone = useMemo(() => {
    return data.task_status === 'complete';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const configButton = isDone
    ? { icon: 'done', outline: false }
    : { icon: 'arrow_forward_ios', outline: true };

  return (
    <a
      href={formattedLink}
      title={clearHtmlTags(title)}
      className={`vitrine-template4__card ${
        isDone ? 'vitrine-template4__card--checked' : ''
      }`}
      key={id}
      data-id={id}
      onClick={event => onClick(event, id)}
      style={styleCardLink}
      data-testid="card"
    >
      <img
        className="vitrine-template4__card-icon"
        src={icon}
        alt={clearHtmlTags(title)}
        data-testid="card-icon"
        width={24}
        height={24}
      />
      {title && (
        <span
          className="vitrine-template4__card-title"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: title }}
        />
      )}
      {description && (
        <span
          className="vitrine-template4__card-description"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      <div className="vitrine-template4__card-footer">
        <p>{data.footer}</p>

        {link && (
          <ButtonWithIcon
            text=""
            icon={{ name: configButton.icon, size: 'small' }}
            isOutlined={configButton.outline}
            data-testid="card-button"
            position="right"
            className="vitrine-template4__card-button"
          />
        )}
      </div>
    </a>
  );
});
